/**
 * Récap journalier admin — MIROIR app de `supabase/functions/_shared/admin-daily-recap.ts`
 * (même découpage en sections que le mail). Parité testée par
 * `supabase/functions/_shared/admin-daily-recap.test.ts` : garder synchro.
 */

export interface RecapStats {
  day: string;
  visits?: {
    /** Visiteurs uniques du jour (migration 0692 ; absents des récaps plus anciens). */
    site_visitors?: number;
    app_visitors?: number;
    visitors?: number;
    site_sessions?: number;
    site_views?: number;
    app_sessions?: number;
    app_views?: number;
    app_ios?: number;
    app_android?: number;
    top_site?: { path: string; views: number }[];
    top_app?: { path: string; views: number }[];
  };
  /** Robots exclus des visites (migration 0692) : pages vues par famille. */
  bots?: { sessions?: number; views?: number; by_family?: Record<string, number> };
  demos?: { started?: number; completed?: number; signup_clicked?: number };
  accounts?: {
    signups?: number;
    signups_by_role?: Record<string, number>;
    logins?: number;
    active?: number;
  };
  purchases?: {
    skillcards?: number;
    skillcards_cents?: number;
    skillcards_by_provider?: Record<string, { count: number; cents: number }>;
    gifts?: number;
    passes?: number;
    passes_by_source?: Record<string, number>;
    subscriptions?: number;
    gifts_redeemed?: number;
  };
  activity?: {
    exercises_completed?: number;
    students_practicing?: number;
    diagnostics?: number;
    automatismes?: number;
    pack_tests?: number;
    live?: number;
    messages?: number;
    reports?: number;
    newsletter?: number;
  };
  /** Tunnels de conversion de la journée (migration 0718 ; absents avant). */
  funnels?: RecapFunnel[];
  prev?: Omit<RecapStats, 'prev'>;
}

export interface RecapFunnel {
  key: string;
  label: string;
  group?: 'entry' | 'buy' | 'invite';
  steps: { key: number; label: string; count: number }[];
}

/** « 8 % » du départ (« — » sans visiteur au départ). */
export function funnelRate(converted: number, started: number): string {
  if (started <= 0) return '—';
  const pct = (converted / started) * 100;
  const v = pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
  return `${String(v).replace('.', ',')} %`;
}

/** Ligne d'un tunnel : convertis sur visiteurs au départ, étapes en détail. */
function funnelLine(f: RecapFunnel, prev: RecapFunnel | undefined): RecapLine {
  const steps = [...f.steps].sort((a, b) => a.key - b.key);
  const started = steps[0]?.count ?? 0;
  const converted = steps[steps.length - 1]?.count ?? 0;
  const prevSteps = prev ? [...prev.steps].sort((a, b) => a.key - b.key) : [];
  return {
    label: f.label,
    value: `${converted} sur ${started} (${funnelRate(converted, started)})`,
    num: converted,
    prevNum: prev ? (prevSteps[prevSteps.length - 1]?.count ?? 0) : undefined,
    detail: steps.map((x) => `${x.label} ${x.count}`).join(' → '),
  };
}

/** « mardi 14 octobre ». */
export function formatRecapDay(day: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${day}T12:00:00Z`));
}

/** « 12,90 € ». */
export function formatEuros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

/** Écart avec la veille : « +3 », « −2 », « = », ou '' si pas de veille. */
export function formatDelta(cur: number, prev: number | undefined): string {
  if (prev === undefined) return '';
  const d = cur - prev;
  if (d === 0) return '=';
  return d > 0 ? `+${d}` : `−${Math.abs(d)}`;
}

const ROLE_LABELS: Record<string, string> = {
  student: 'élève',
  parent: 'parent',
  tutor: 'prof',
  player: 'curieux',
  admin: 'admin',
  sans_role: 'sans rôle',
};

const BOT_LABELS: Record<string, string> = {
  google: 'Google',
  bing: 'Bing',
  apple: 'Apple',
  yandex: 'Yandex',
  baidu: 'Baidu',
  duckduckgo: 'DuckDuckGo',
  meta: 'Meta',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  perplexity: 'Perplexity',
  bytedance: 'ByteDance',
  seo: 'outils SEO',
  monitor: 'surveillance',
  headless: 'navigateur sans écran',
  webdriver: 'navigateur piloté',
  burst: 'rafales',
  no_ua: 'sans agent',
  other: 'autres',
};

/** Visiteurs uniques ; repli sur les visites pour un récap d'avant la migration 0692. */
function visitorsOf(
  v: NonNullable<RecapStats['visits']> | undefined,
  key: 'site' | 'app',
): number | undefined {
  return key === 'site' ? v?.site_visitors : v?.app_visitors;
}

/** Total des visiteurs du jour (repli : visites site + app pour un ancien récap). */
export function recapVisitors(v: RecapStats['visits'] | undefined): number {
  if (v?.visitors !== undefined) return v.visitors;
  return (v?.site_sessions ?? 0) + (v?.app_sessions ?? 0);
}

const SOURCE_LABELS: Record<string, string> = {
  stripe: 'Stripe',
  parent_payment: 'payé par un parent',
  iap_ios: 'App Store',
  iap_android: 'Google Play',
  play_store: 'Google Play',
  app_store: 'App Store',
};

export interface RecapLine {
  label: string;
  value: string;
  /** Valeur numérique comparée à la veille (pas de comparaison si absente). */
  num?: number;
  prevNum?: number;
  detail?: string;
}

export interface RecapSection {
  title: string;
  lines: RecapLine[];
}

const n = (v: number | undefined) => v ?? 0;

function breakdown(
  map: Record<string, number> | undefined,
  labels: Record<string, string>,
): string {
  const parts = Object.entries(map ?? {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} ${labels[k] ?? k}`);
  return parts.join(', ');
}

/** Les sections du récap (mail et carte du feed). */
export function recapSections(s: RecapStats): RecapSection[] {
  const p = s.prev;
  const v = s.visits ?? {};
  const pv = p?.visits ?? {};
  const top = (list: { path: string; views: number }[] | undefined) =>
    (list ?? [])
      .slice(0, 3)
      .map((t) => `${t.path} (${t.views})`)
      .join(', ');
  const appDetail = [
    n(v.app_ios) ? `${n(v.app_ios)} iOS` : '',
    n(v.app_android) ? `${n(v.app_android)} Android` : '',
  ]
    .filter(Boolean)
    .join(', ');

  const pu = s.purchases ?? {};
  const ppu = p?.purchases ?? {};
  const providers = Object.fromEntries(
    Object.entries(pu.skillcards_by_provider ?? {}).map(([k, x]) => [k, x.count]),
  );

  return [
    {
      title: 'Visites',
      lines: [
        ...(['site', 'app'] as const).map((key): RecapLine => {
          const sessions = n(key === 'site' ? v.site_sessions : v.app_sessions);
          const views = n(key === 'site' ? v.site_views : v.app_views);
          const visitors = visitorsOf(v, key);
          const prevVisitors = visitorsOf(pv, key);
          // Ancien récap (avant 0692) : visites seulement, robots compris.
          if (visitors === undefined)
            return {
              label: key === 'site' ? 'Site' : 'App',
              value: `${sessions} visites · ${views} pages`,
              num: sessions,
              prevNum: p ? n(key === 'site' ? pv.site_sessions : pv.app_sessions) : undefined,
              detail:
                key === 'site'
                  ? top(v.top_site)
                  : [appDetail, top(v.top_app)].filter(Boolean).join(' — '),
            };
          return {
            label: key === 'site' ? 'Site' : 'App',
            value: `${visitors} visiteur${visitors > 1 ? 's' : ''} · ${sessions} visites · ${views} pages`,
            num: visitors,
            // La veille n'est comparable que si elle compte aussi les visiteurs.
            prevNum: prevVisitors,
            detail:
              key === 'site'
                ? top(v.top_site)
                : [appDetail, top(v.top_app)].filter(Boolean).join(' — '),
          };
        }),
        ...(s.bots
          ? [
              {
                label: 'Robots (exclus)',
                value: `${n(s.bots.views)} pages`,
                detail: breakdown(
                  Object.fromEntries(
                    Object.entries(s.bots.by_family ?? {}).sort((a, b) => b[1] - a[1]),
                  ),
                  BOT_LABELS,
                ),
              },
            ]
          : []),
      ],
    },
    {
      title: 'Démos',
      lines: [
        {
          label: 'Lancées',
          value: String(n(s.demos?.started)),
          num: n(s.demos?.started),
          prevNum: p ? n(p.demos?.started) : undefined,
        },
        {
          label: 'Terminées',
          value: String(n(s.demos?.completed)),
          num: n(s.demos?.completed),
          prevNum: p ? n(p.demos?.completed) : undefined,
        },
        {
          label: 'Clics « créer un compte »',
          value: String(n(s.demos?.signup_clicked)),
          num: n(s.demos?.signup_clicked),
          prevNum: p ? n(p.demos?.signup_clicked) : undefined,
        },
      ],
    },
    {
      title: 'Comptes',
      lines: [
        {
          label: 'Inscriptions',
          value: String(n(s.accounts?.signups)),
          num: n(s.accounts?.signups),
          prevNum: p ? n(p.accounts?.signups) : undefined,
          detail: breakdown(s.accounts?.signups_by_role, ROLE_LABELS),
        },
        {
          label: 'Connexions',
          value: String(n(s.accounts?.logins)),
          num: n(s.accounts?.logins),
          prevNum: p ? n(p.accounts?.logins) : undefined,
        },
        {
          label: 'Comptes actifs',
          value: String(n(s.accounts?.active)),
          num: n(s.accounts?.active),
          prevNum: p ? n(p.accounts?.active) : undefined,
        },
      ],
    },
    {
      title: 'Achats',
      lines: [
        {
          label: 'SKILLCARDS',
          value: `${n(pu.skillcards)} · ${formatEuros(n(pu.skillcards_cents))}`,
          num: n(pu.skillcards),
          prevNum: p ? n(ppu.skillcards) : undefined,
          detail: [
            breakdown(providers, SOURCE_LABELS),
            n(pu.gifts) ? `${n(pu.gifts)} carte(s) cadeau` : '',
          ]
            .filter(Boolean)
            .join(', '),
        },
        {
          label: 'Pass',
          value: String(n(pu.passes)),
          num: n(pu.passes),
          prevNum: p ? n(ppu.passes) : undefined,
          detail: breakdown(pu.passes_by_source, SOURCE_LABELS),
        },
        {
          label: 'Abonnements',
          value: String(n(pu.subscriptions)),
          num: n(pu.subscriptions),
          prevNum: p ? n(ppu.subscriptions) : undefined,
        },
        {
          label: 'Cadeaux activés',
          value: String(n(pu.gifts_redeemed)),
          num: n(pu.gifts_redeemed),
          prevNum: p ? n(ppu.gifts_redeemed) : undefined,
        },
      ],
    },
    ...(s.funnels
      ? [
          {
            title: 'Conversions',
            lines: s.funnels
              .filter((f) => (f.steps[0]?.count ?? 0) > 0)
              .map((f) =>
                funnelLine(
                  f,
                  p?.funnels?.find((x) => x.key === f.key),
                ),
              ),
          },
        ]
      : []),
    {
      title: 'Activité',
      lines: (
        [
          ['Exercices terminés', 'exercises_completed'],
          ['Élèves qui ont travaillé', 'students_practicing'],
          ['Diagnostics', 'diagnostics'],
          ['Séries d’automatismes', 'automatismes'],
          ['Tests de pack', 'pack_tests'],
          ['Participations aux lives', 'live'],
          ['Messages', 'messages'],
          ['Signalements', 'reports'],
          ['Inscriptions newsletter', 'newsletter'],
        ] as const
      ).map(([label, key]) => {
        const k: keyof NonNullable<RecapStats['activity']> = key;
        const cur = n(s.activity?.[k]);
        return { label, value: String(cur), num: cur, prevNum: p ? n(p.activity?.[k]) : undefined };
      }),
    },
  ];
}
